'use client'

import React, { useState, useCallback, useEffect } from 'react'
import usePlacesAutocomplete, { getGeocode, getLatLng } from 'use-places-autocomplete'
import { Command } from 'cmdk'
import * as PopoverPrimitive from '@radix-ui/react-popover'
import { Check, ChevronDown, MapPin } from 'lucide-react'
import { AddressData, BaseFieldProps, FieldErrors } from '../types'
import { validateAddress } from '../utils/validation'
import { cn, baseInputStyles, inputVariants, labelStyles, errorStyles, helperTextStyles, buttonStyles, buttonVariants, cardStyles, cardHeaderStyles, cardContentStyles } from '../utils/styles'
import { COUNTRIES } from '../utils/countries'

export interface AddressInputProps extends BaseFieldProps {
  address?: Partial<AddressData>
  onChange: (address: AddressData) => void
  errors?: FieldErrors<AddressData>
  googleMapsApiKey?: string
  showCoordinates?: boolean
  description?: string
  validateOnChange?: boolean
}


export function AddressInput({
  address = {},
  onChange,
  errors = {},
  googleMapsApiKey,
  required = false,
  label = 'Address',
  description,
  showCoordinates = false,
  disabled = false,
  className,
  error,
  helperText,
  validateOnChange = true
}: AddressInputProps) {
  const [useAutoComplete, setUseAutoComplete] = useState(!!googleMapsApiKey)
  const [isValidated, setIsValidated] = useState(false)
  const [internalErrors, setInternalErrors] = useState<FieldErrors<AddressData>>({})
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)

  // Use the places autocomplete hook
  const {
    ready,
    value,
    suggestions: { status, data },
    setValue,
    clearSuggestions,
  } = usePlacesAutocomplete({
    requestOptions: {
      componentRestrictions: { country: ['ch', 'fr', 'de', 'it', 'at', 'us', 'gb'] },
    },
    debounce: 300,
    cache: 24 * 60 * 60, // Cache for 24 hours
  })

  // Load Google Maps script if API key is provided
  useEffect(() => {
    if (googleMapsApiKey && !(window as any).google) {
      const script = document.createElement('script')
      script.src = `https://maps.googleapis.com/maps/api/js?key=${googleMapsApiKey}&libraries=places`
      script.async = true
      script.defer = true
      document.head.appendChild(script)

      return () => {
        if (document.head.contains(script)) {
          document.head.removeChild(script)
        }
      }
    }
  }, [googleMapsApiKey])

  // Handle address selection from Google Places
  const handleSelect = useCallback(
    async (description: string) => {
      setValue(description, false)
      clearSuggestions()

      try {
        const results = await getGeocode({ address: description })
        const { lat, lng } = await getLatLng(results[0])

        // Parse the Google Places result
        const place = results[0]
        const components = place.address_components || []

        const getComponent = (types: string[]) => {
          const component = components.find((c: any) =>
            types.some(type => c.types.includes(type))
          )
          return component?.long_name || ''
        }

        const addressData: AddressData = {
          streetLine1: `${getComponent(['street_number'])} ${getComponent(['route'])}`.trim(),
          streetLine2: '',
          city: getComponent(['locality', 'administrative_area_level_2']),
          state: getComponent(['administrative_area_level_1']),
          postalCode: getComponent(['postal_code']),
          country: getComponent(['country']),
          fullFormattedAddress: place.formatted_address || description,
          placeId: place.place_id,
          coordinates: { lat, lng }
        }

        onChange(addressData)
        setUseAutoComplete(true)
      } catch (error) {
        console.warn('Error geocoding address:', error)
        // Fallback to manual entry
        setUseAutoComplete(false)
      }
    },
    [setValue, clearSuggestions, onChange]
  )

  // Handle manual input change
  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value
    setValue(inputValue)

    // If autocomplete is not being used, update address manually
    if (!useAutoComplete || !ready) {
      onChange({
        ...address,
        fullFormattedAddress: inputValue
      } as AddressData)
    }
  }

  // Handle manual address field changes
  const handleFieldChange = (field: keyof AddressData, value: string) => {
    const newAddress = { ...address, [field]: value }
    onChange(newAddress as AddressData)

    if (validateOnChange) {
      const fieldErrors = validateAddress(newAddress, required)
      setInternalErrors(fieldErrors)
    }
  }

  // Validate address
  const validateCurrentAddress = useCallback(() => {
    const fieldErrors = validateAddress(address, required)
    setInternalErrors(fieldErrors)
    setIsValidated(true)
    return Object.keys(fieldErrors).length === 0
  }, [address, required])

  // Get display errors (props errors override internal errors)
  const displayErrors = Object.keys(errors).length > 0 ? errors : internalErrors
  const hasErrors = Object.keys(displayErrors).length > 0

  return (
    <div className={cn('space-y-4', className)}>
      {/* Header */}
      <div className={cardHeaderStyles}>
        <div className="flex items-center justify-between">
          <div>
            {label && (
              <label className={labelStyles}>
                {label}
                {required && <span className="text-red-500 ml-1">*</span>}
              </label>
            )}
            {description && (
              <p className={helperTextStyles}>{description}</p>
            )}
          </div>

          {googleMapsApiKey && (
            <button
              type="button"
              onClick={() => setUseAutoComplete(!useAutoComplete)}
              disabled={disabled}
              className={cn(
                buttonStyles,
                buttonVariants.outline,
                'text-xs px-2 py-1'
              )}
            >
              {useAutoComplete ? 'Manual Entry' : 'Use Autocomplete'}
            </button>
          )}
        </div>
      </div>

      <div className={cardStyles}>
        <div className={cardContentStyles}>
          {/* Address Search/Input */}
          {useAutoComplete && googleMapsApiKey ? (
            <div className="space-y-2">
              <label className={labelStyles}>Search Address</label>
              <PopoverPrimitive.Root open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
                <PopoverPrimitive.Trigger asChild>
                  <div className="relative">
                    <input
                      type="text"
                      value={value}
                      onChange={(e) => {
                        handleInputChange(e)
                        setIsPopoverOpen(e.target.value.length > 0 && status === 'OK' && data.length > 0)
                      }}
                      onFocus={() => {
                        if (value.length > 0 && status === 'OK' && data.length > 0) {
                          setIsPopoverOpen(true)
                        }
                      }}
                      disabled={!ready || disabled}
                      placeholder="Start typing an address..."
                      className={cn(
                        baseInputStyles,
                        hasErrors ? inputVariants.error : inputVariants.default,
                        disabled && inputVariants.disabled,
                        'pr-8'
                      )}
                    />
                    <MapPin className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  </div>
                </PopoverPrimitive.Trigger>

                <PopoverPrimitive.Portal>
                  <PopoverPrimitive.Content
                    className="z-50 w-[var(--radix-popover-trigger-width)] bg-white border border-gray-200 rounded-md shadow-lg overflow-hidden"
                    sideOffset={4}
                    align="start"
                  >
                    <Command className="max-h-60">
                      <Command.List>
                        {status === 'OK' && data.length > 0 && (
                          <Command.Group>
                            {data.map((suggestion: any) => (
                              <Command.Item
                                key={suggestion.place_id}
                                value={suggestion.description}
                                onSelect={() => {
                                  handleSelect(suggestion.description)
                                  setIsPopoverOpen(false)
                                }}
                                className="relative flex cursor-pointer select-none items-center px-4 py-2 text-sm hover:bg-gray-50 data-[selected]:bg-gray-50"
                              >
                                <MapPin className="mr-3 h-4 w-4 text-gray-400" />
                                <span className="flex-1">{suggestion.description}</span>
                              </Command.Item>
                            ))}
                          </Command.Group>
                        )}
                        {status === 'OK' && data.length === 0 && (
                          <Command.Empty className="py-4 text-center text-sm text-gray-500">
                            No addresses found.
                          </Command.Empty>
                        )}
                      </Command.List>
                    </Command>
                  </PopoverPrimitive.Content>
                </PopoverPrimitive.Portal>
              </PopoverPrimitive.Root>
            </div>
          ) : null}

          {/* Manual Address Entry */}
          {(!useAutoComplete || !googleMapsApiKey) && (
            <div className="space-y-4">
              {/* Street Line 1 */}
              <div>
                <label className={labelStyles}>
                  Street Address
                  {required && <span className="text-red-500 ml-1">*</span>}
                </label>
                <input
                  type="text"
                  value={address.streetLine1 || ''}
                  onChange={(e) => handleFieldChange('streetLine1', e.target.value)}
                  disabled={disabled}
                  placeholder="123 Main Street"
                  className={cn(
                    baseInputStyles,
                    displayErrors.streetLine1 ? inputVariants.error : inputVariants.default,
                    disabled && inputVariants.disabled
                  )}
                />
                {displayErrors.streetLine1 && (
                  <p className={errorStyles}>{displayErrors.streetLine1}</p>
                )}
              </div>

              {/* Street Line 2 */}
              <div>
                <label className={labelStyles}>Street Address 2 (Optional)</label>
                <input
                  type="text"
                  value={address.streetLine2 || ''}
                  onChange={(e) => handleFieldChange('streetLine2', e.target.value)}
                  disabled={disabled}
                  placeholder="Apartment, suite, etc."
                  className={cn(
                    baseInputStyles,
                    inputVariants.default,
                    disabled && inputVariants.disabled
                  )}
                />
              </div>

              {/* City and State */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelStyles}>
                    City
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="text"
                    value={address.city || ''}
                    onChange={(e) => handleFieldChange('city', e.target.value)}
                    disabled={disabled}
                    placeholder="Zurich"
                    className={cn(
                      baseInputStyles,
                      displayErrors.city ? inputVariants.error : inputVariants.default,
                      disabled && inputVariants.disabled
                    )}
                  />
                  {displayErrors.city && (
                    <p className={errorStyles}>{displayErrors.city}</p>
                  )}
                </div>

                <div>
                  <label className={labelStyles}>
                    State/Province/Canton
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="text"
                    value={address.state || ''}
                    onChange={(e) => handleFieldChange('state', e.target.value)}
                    disabled={disabled}
                    placeholder="ZH"
                    className={cn(
                      baseInputStyles,
                      displayErrors.state ? inputVariants.error : inputVariants.default,
                      disabled && inputVariants.disabled
                    )}
                  />
                  {displayErrors.state && (
                    <p className={errorStyles}>{displayErrors.state}</p>
                  )}
                </div>
              </div>

              {/* Postal Code and Country */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className={labelStyles}>
                    Postal Code
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <input
                    type="text"
                    value={address.postalCode || ''}
                    onChange={(e) => handleFieldChange('postalCode', e.target.value)}
                    disabled={disabled}
                    placeholder="8001"
                    className={cn(
                      baseInputStyles,
                      displayErrors.postalCode ? inputVariants.error : inputVariants.default,
                      disabled && inputVariants.disabled
                    )}
                  />
                  {displayErrors.postalCode && (
                    <p className={errorStyles}>{displayErrors.postalCode}</p>
                  )}
                </div>

                <div>
                  <label className={labelStyles}>
                    Country
                    {required && <span className="text-red-500 ml-1">*</span>}
                  </label>
                  <select
                    value={address.country || ''}
                    onChange={(e) => handleFieldChange('country', e.target.value)}
                    disabled={disabled}
                    className={cn(
                      baseInputStyles,
                      displayErrors.country ? inputVariants.error : inputVariants.default,
                      disabled && inputVariants.disabled
                    )}
                  >
                    <option value="">Select a country</option>
                    {COUNTRIES.map((country) => (
                      <option key={country.code} value={country.name}>
                        {country.name}
                      </option>
                    ))}
                  </select>
                  {displayErrors.country && (
                    <p className={errorStyles}>{displayErrors.country}</p>
                  )}
                </div>
              </div>
            </div>
          )}

          {/* Coordinates Display */}
          {showCoordinates && address.coordinates && (
            <div className="mt-4 p-3 bg-gray-50 rounded-md">
              <h4 className="text-sm font-medium text-gray-700 mb-2">Coordinates</h4>
              <div className="text-sm text-gray-600">
                <p>Latitude: {address.coordinates.lat}</p>
                <p>Longitude: {address.coordinates.lng}</p>
              </div>
            </div>
          )}

          {/* Validation Button */}
          <div className="flex justify-between items-center mt-4">
            <button
              type="button"
              onClick={validateCurrentAddress}
              disabled={disabled}
              className={cn(
                buttonStyles,
                hasErrors ? buttonVariants.outline : buttonVariants.primary
              )}
            >
              {isValidated ? (hasErrors ? 'Fix Errors' : 'Validated ✓') : 'Validate Address'}
            </button>

            {/* Summary */}
            {address.fullFormattedAddress && (
              <div className="text-sm text-gray-600 max-w-md">
                <strong>Selected:</strong> {address.fullFormattedAddress}
              </div>
            )}
          </div>

          {/* General Error */}
          {error && <p className={errorStyles}>{error}</p>}
          {helperText && <p className={helperTextStyles}>{helperText}</p>}
        </div>
      </div>
    </div>
  )
}

// Export types
export type { AddressData }